// Note: If this UI & functionality is ever required elsewhere in the project, this component
// including children can be moved to a more centralised location
// TODO: Consider extracting out some of this functionality so it's more readable
import { useContext, useState, useEffect, useReducer } from 'react';
import { HookFormContext } from 'components/cef_form/hook-form/providers/HookFormProvider';
import InnerFormSection from 'components/ui-components/forms/InnerFormSection';
import HookFormTextInputWithButton from 'components/cef_form/hook-form/HookFormTextInputWithButton';
import HookFormTextInput from 'components/cef_form/hook-form/HookFormTextInput';
import AlertBox from 'components/ui-components/AlertBox';
import updateMultipleFormValues from 'utils/hook-form/updateMultipleFormValues';
import TextLoader from 'components/ui-components/loaders/TextLoader';
import { useHTTP } from 'hooks/useRequest';
import GoogleRecaptcha from 'components/reCAPTCHA/GoogleRecaptcha';
import CompanyDataPanel from './CompanyDataPanel';
import {
  mapAddressToFields,
  resetAddressFields,
} from '../../postcodeSelectors/addressDataModels';
import {
  companyNumberSelectorInitialState,
  companyNumberSelectorReducer,
} from './companyNumberSelectorStateManager';

const CompanyNumberSelector = () => {
  const {
    trigger,
    setValue,
    errors,
    setError,
    clearErrors,
    recaptchaKeys,
    bypassRecaptcha,
    watchedFields: {
      companyNumber,
      companyNumberSearched,
      companyNumberLookupSuccess,
      companyName,
      companyVatNumber,
      addressOne,
    },
  } = useContext(HookFormContext);

  const [v2Key, v3Key] = recaptchaKeys;

  const [response, setResponse] = useState({ status: undefined, data: {} });
  const [token, setToken] = useState();

  // Set up reducer, and persist certain data
  const [state, dispatch] = useReducer(companyNumberSelectorReducer, {
    ...companyNumberSelectorInitialState,
    showCompanyData: companyNumberLookupSuccess,
    showManualEntry: companyNumberSearched && !companyNumberLookupSuccess,
    showVatField: !!companyNumber,
  });

  const {
    loading,
    userClickedSearch,
    showCompanyData,
    showManualEntry,
    showVatField,
    apiErrorMessage,
  } = state;

  const setLoading = isLoading => {
    dispatch({ type: 'SET_IS_LOADING', payload: isLoading });
  };

  const requestConfig = {
    url: token
      ? `/company_details/${companyNumber}?g-recaptcha-response=${token}`
      : null,
    method: 'get',
  };

  const { data: res, error, loading: loadingData } = useHTTP(requestConfig);

  // Handle loading state from companies house api call
  useEffect(() => {
    if (setLoading) {
      setLoading(loadingData);
    }
  }, [loading]);

  useEffect(() => {
    setToken('');
    if (res) {
      // a successful res will return an object
      if (typeof res === 'object' && !Array.isArray(res)) {
        setResponse({ status: 200, data: res });
        // a failed res will return an array
      } else if (res.length >= 1) {
        // successful call and issue with data return error
        setResponse({
          status: res[1].status,
          data: res[0].error,
        });
      }
    }
  }, [res]);

  useEffect(() => {
    setToken('');
    if (error === undefined) return;
    // recaptcha fails, call never made, show v2
    if (error?.error.status === 400) {
      // This enables manual entry
      setResponse({
        status: 400,
        data: error.error.data.error || 'recaptcha failed',
        responseType: error.error.data.response_type,
      });
    } else if (error?.error.status === 404 || error?.error.status === 503) {
      // call fails but recaptcha successful return error
      setResponse({
        status: error.error.status,
        data: error.error.statusText,
      });
    }
  }, [error]);

  // Unset the API error if shown previously so that it won't show when the user comes back to edit
  // the manual fields - it'll only show when the API fail initially happens
  useEffect(() => {
    dispatch({ type: 'UNSET_API_ERROR' });
  }, []);

  const resetCompanyData = () => {
    dispatch({ type: 'RESET' });

    updateMultipleFormValues(
      setValue,
      {
        // Validation values
        companyNumberSearched: false,
        companyNumberLookupSuccess: false,
        billingAddressMatchesRegistered: true,
        // Data values
        company_number: '',
        company_name: '',
        company_vat_number: '',
      },
      resetAddressFields(1),
      resetAddressFields(2),
    );

    clearErrors('company_number');
  };

  const fetchCompanyData = async () => {
    if (userClickedSearch) {
      dispatch({ type: 'UNSET_CLICKED_SEARCH' });
    }
    // We need to reset the manual and address values here for if the user tries to use the search
    // again when they have already had an API failure (when coming back from summary or navigating
    // via browser buttons)
    updateMultipleFormValues(
      setValue,
      {
        // Validation values
        billingAddressMatchesRegistered: true,
        // Data values
        company_name: '',
        company_vat_number: '',
      },
      resetAddressFields(1),
      resetAddressFields(2),
    );

    // Check for length constraints
    if (companyNumber.length < 8 && !/^[a-zA-Z]/.test(companyNumber)) {
      /**
       * company numbers must be 8 characters long, usually this is done by adding 0's to the start
       * although alot of users will only know there specific number e.g. 12345
       * this function adds the additional 0's to form a complete number 00012345
       */
      const newValue = companyNumber.padStart(8, '0');
      setValue('company_number', newValue);
    }

    await trigger('company_number'); // Trigger validation for the field

    const companyNumberIsValid =
      errors?.company_number?.message ===
        'Please search for your customer number' ||
      !errors?.company_number?.message;

    if (companyNumberIsValid && companyNumber) {
      dispatch({ type: 'SET_CLICKED_SEARCH' });
      clearErrors('company_number');
    }
  };

  const validateCompanyNumberField = () => {
    // Check if the search button has been pressed
    if (!companyNumberSearched) {
      return 'Please search for your customer number';
    }

    // Stop user progressing if API error AlertBox is present on the DOM, but not for manual entry
    if (apiErrorMessage && !showManualEntry) {
      return ''; // Display no message as it's handled by AlertBox
    }

    return true; // Validation passed
  };

  // Scenario handlers
  const handleCompanyData = (
    foundCompanyNumber,
    foundCompanyName,
    foundAddress,
  ) => {
    updateMultipleFormValues(setValue, {
      // Validation values
      companyNumberSearched: true,
      companyNumberLookupSuccess: true,
      // Data values
      company_number: foundCompanyNumber,
      company_name: foundCompanyName,
      // Set address 1
      ...mapAddressToFields(1, foundAddress),
    });

    dispatch({ type: 'SET_SUCCESS' });
  };

  const handleManualEntry = () => {
    setValue('companyNumberSearched', true);

    dispatch({ type: 'SET_MANUAL' });
  };

  const handleFieldErrorOnly = errorMessage => {
    setValue('companyNumberSearched', false);

    setError('company_number', {
      type: 'validate',
      message: errorMessage,
    });

    dispatch({ type: 'RESET' });
  };

  const handleAlertErrorOnly = errorMessage => {
    setValue('companyNumberSearched', true);

    // We just need to stop the user progressing here. No error message text is set, as AlertBox
    // component is used to handle the error message instead
    setError('company_number', {
      type: 'required',
    });

    dispatch({
      type: 'SET_ALERT_ERROR_ONLY',
      payload: { title: errorMessage },
    });
  };

  useEffect(() => {
    const { status, data } = response;

    if (status) {
      // Reset the clicked state after response received
      dispatch({ type: 'UNSET_CLICKED_SEARCH' });

      const {
        company_number: foundCompanyNumber,
        company_name: foundCompanyName,
        address: foundAddress,
      } = data;

      // HAPPY PATH
      // Note: If company chosen is inactive, allow to progress anyway
      if (status === 200) {
        handleCompanyData(foundCompanyNumber, foundCompanyName, foundAddress);
      }
      // UNHAPPY PATHS
      else if (
        (status === 400 &&
          data === 'If the error persists please contact customer services.') ||
        status === 403 || // Recaptcha not verified
        status === 404 || // Not found
        status === 503 // Service unavailable
      ) {
        handleManualEntry();
      }
      // Number hit the endpoint and didn't match
      else if (data.includes('valid company number')) {
        // Add a standard field error to Company Number field
        handleFieldErrorOnly(data);
      }
      // Number detected is a charity
      else if (status === 400 && data.includes('charity')) {
        // Set a custom alert error, not standard field error
        // TODO: We might want set custom text if a charity so we can add a link for the user
        handleAlertErrorOnly(data);
      }
      // Recaptcha fail
      else if (data === 'recaptcha failed') {
        // Do nothing
      }
    }
  }, [response]);

  return (
    <InnerFormSection title="Company Information">
      <HookFormTextInputWithButton
        className="mt-10"
        id="company_number"
        label="Company Number"
        description={`You can search the <a class='td-u' href='https://find-and-update.company-information.service.gov.uk' target='_blank'>Companies House Register</a> to find your company number.`}
        placeholder="e.g. 123456"
        value={companyNumber}
        hideControls={showCompanyData}
        buttonClassName="cef-btn cef-btn--small-feature cef-btn--grey cef-btn--med"
        buttonText="Search"
        rules={{
          required: 'Please enter a valid company number',
          maxLength: {
            value: 8,
            message: 'Must be between 6 and 8 characters long',
          },
          pattern: {
            value: /^[a-zA-Z0-9]+$/,
            message: 'Must contain only alphanumeric characters',
          },
          validate: validateCompanyNumberField, // Custom validation
        }}
        onClick={fetchCompanyData}
        disabled={userClickedSearch && token !== ''}
      />

      {loading && <TextLoader className="mt-10" />}

      <GoogleRecaptcha
        res={res}
        error={error}
        submit={userClickedSearch}
        action="company_lookup"
        bypassRecaptcha={bypassRecaptcha}
        siteKeyV3={v3Key}
        siteKeyV2={v2Key}
        setToken={setToken}
        className="mt-8"
        v2Error="Please complete the reCaptcha above"
        v2ErrorClass="cef-ui-input-error cef-ui-input__error mb-0"
      />

      {/* If company lookup succeeded after searching, show company details */}
      {showCompanyData && (
        <CompanyDataPanel
          companyName={companyName}
          address={addressOne}
          resetCompanyData={resetCompanyData}
        />
      )}

      {apiErrorMessage && (
        <AlertBox
          className="mt-16 mb-20"
          title={apiErrorMessage.title}
          body={apiErrorMessage.body || null}
        />
      )}

      {/* If company lookup failed after searching, force manual entry */}
      {showManualEntry && (
        <HookFormTextInput
          className="mt-20"
          id="company_name"
          label="Company Name"
          value={companyName}
          rules={{
            required: 'Please enter your company name',
          }}
        />
      )}

      {/* Display regardless of result after searching (not if charity, or number doesn't exist) */}
      {showVatField && (
        <HookFormTextInput
          className="mt-20"
          id="company_vat_number"
          label="Company VAT Number <span>(if applicable)</span>"
          value={companyVatNumber}
        />
      )}
    </InnerFormSection>
  );
};

export default CompanyNumberSelector;
